import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {AssayRunSchema, ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import SchemaSelector from 'samples/SchemaSelector';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import Modal from 'ui-library/modals/Modal';

import {
  AutomationOutputFileResultSchemaInfo,
  ClientAutomationOutputFileConfigSpec,
  TransformationFieldInfo,
  TransformationFieldType,
} from '../RunSchemaSpecConstants';
import {ResultSchemaInfoModalQuery} from './__generated/ResultSchemaInfoModalQuery';
import {convertResultSchemaToDatasetConfigString} from './DatasetConfigConstants';
import SchemaInfoFieldsSection from './SchemaInfoFieldsSection';
import {openSetColumnsMapModal} from './SetColumnsMapEditor';

export const RESULT_SCHEMA_INFO_MODAL_QUERY = gql`
  query ResultSchemaInfoModalQuery @skipAudit @engComponent(name: LAB_AUTOMATION) {
    ...AllResultSchemasGraphQL
  }
  ${getFragmentWrapper('AllResultSchemas').fragment}
`;

export interface ResultSchemaInfoModalProps {
  baseSchema: AssayRunSchema | null;
  outputFileConfig: ClientAutomationOutputFileConfigSpec;
  onUpdateConfig: (newConfig: ClientAutomationOutputFileConfigSpec) => void;
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  ownerId: string;
}

export default function ResultSchemaInfoModal(props: ResultSchemaInfoModalProps): JSX.Element {
  const schemasQueryResult = useBenchlingQuery<ResultSchemaInfoModalQuery>(RESULT_SCHEMA_INFO_MODAL_QUERY);
  const [outputFileConfig, setOutputFileConfig] = useState(props.outputFileConfig);
  const resultSchemaInfo = outputFileConfig.resultSchemaInfo!;
  const setResultSchemaInfo = (
    newResultSchemaInfo: AutomationOutputFileResultSchemaInfo,
    newOutputFileConfig: ClientAutomationOutputFileConfigSpec
  ): void => {
    setOutputFileConfig({...newOutputFileConfig, resultSchemaInfo: newResultSchemaInfo});
  };
  let allResultSchemas: Array<ResultSchema> = [];
  let selectedSchema: ResultSchema | null = null;
  if (schemasQueryResult.type === QueryResultType.SUCCESS) {
    allResultSchemas = graphQLArrayToREST('ResultSchema', schemasQueryResult.data.resultSchemas);
    selectedSchema = resultSchemaInfo.schema
      ? (allResultSchemas.find((schema) => schema.id === resultSchemaInfo.schema) ?? null)
      : null;
  }
  return (
    <Modal
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onUpdateConfig(outputFileConfig);
        props.onRequestSubmit();
      }}
      title="Configure results"
      buttons={[
        {
          text: 'Create new result schema',
          submit: false,
          style: resultSchemaInfo.schema == null ? ButtonStyle.SECONDARY : ButtonStyle.TERTIARY_BLUE,
          onClick: () => {
            openSetColumnsMapModal({
              ...props,
              onUpdateConfig: setOutputFileConfig,
            });
          },
        },
        {
          text: 'Done',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: resultSchemaInfo.schema == null,
        },
      ]}
    >
      {schemasQueryResult.type === QueryResultType.SUCCESS ? (
        <>
          <div className={css(styles.schemaSelector)}>
            <FieldLabel label="Result schema" labelStyle={LabelStyle.LABEL} isRequired={true} />
            <br />
            <SchemaSelector
              isDisabled={false}
              onChange={(schema: ResultSchema) => {
                // need to infer new dataset column mappings if schema changes and dataset is being created
                const newOutputFileConfig = outputFileConfig.shouldCreateDataset
                  ? {
                      ...outputFileConfig,
                      datasetConfigJson: convertResultSchemaToDatasetConfigString(schema),
                    }
                  : outputFileConfig;
                setResultSchemaInfo(
                  {...resultSchemaInfo, schema: schema.id, fields: {}},
                  newOutputFileConfig
                );
              }}
              schemas={allResultSchemas}
              selectedSchemaId={resultSchemaInfo.schema}
            />
          </div>
          <SchemaInfoFieldsSection
            schema={selectedSchema}
            fields={outputFileConfig.resultSchemaInfo!.fields}
            allTagSchemas={[]}
            allFieldsets={[]}
            allowedFieldTypes={[
              TransformationFieldType.DIRECT,
              TransformationFieldType.PRIMARY_SAMPLE_ENTITY,
            ]}
            onUpdateField={(fieldName: string, fieldInfo: TransformationFieldInfo | null) => {
              const newFields = {...resultSchemaInfo.fields};
              if (fieldInfo) {
                newFields[fieldName] = fieldInfo;
              } else {
                delete newFields[fieldName];
              }
              setResultSchemaInfo({...resultSchemaInfo, fields: newFields}, outputFileConfig);
            }}
          />
        </>
      ) : (
        <CenteredHorizontalThrobber />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  schemaSelector: {
    marginBottom: '20px',
  },
});
